import Text from 'core/elements/Text'
import { formatDate } from 'utils/misc'
import {
  VMHABannerState,
  VMHAEvent,
  VMHAEventPhase,
  VMHAEventResult,
  VMHAEventsResponse,
  VMHAHostStatus,
  VMHATaskDetail,
  VMHATaskDetailsResponse,
  VMHATaskPhase,
  VMHATaskResult,
} from './types'

/**
 * Parses VMHA events response and converts to VMHAEvent objects
 */
export const parseVMHAEventsResponse = (
  response: VMHAEventsResponse,
): Map<string, VMHAEventResult[]> => {
  const eventsByHost = new Map<string, VMHAEventResult[]>()

  if (!response?.data?.result || response.data.result.length === 0) {
    return eventsByHost
  }

  response.data.result.forEach((event) => {
    const hostId = event.metric.host_id
    if (!eventsByHost.has(hostId)) {
      eventsByHost.set(hostId, [])
    }
    eventsByHost.get(hostId)!.push(event)
  })

  return eventsByHost
}

/**
 * Gets the oldest event for each unique vmha_uuid
 * Returns a list of events, one per unique vmha_uuid with the oldest timestamp
 */
export const getFirstEventForHost = (
  events: VMHAEventResult[],
  resMgrHosts: (id: string) => any,
): VMHAEvent[] => {
  if (!events || events.length === 0) {
    return []
  }

  // Group events by vmha_uuid and find the oldest event for each
  const oldestByUuid = new Map<string, VMHAEventResult>()

  events.forEach((event) => {
    const uuid = event.metric.vmha_uuid
    const currentTs = parseFloat(event.metric.ts)

    if (!oldestByUuid.has(uuid)) {
      oldestByUuid.set(uuid, event)
    } else {
      const existingTs = parseFloat(oldestByUuid.get(uuid)!.metric.ts)
      if (currentTs < existingTs) {
        oldestByUuid.set(uuid, event)
      }
    }
  })

  // Convert to VMHAEvent array
  return Array.from(oldestByUuid.values()).map((oldestEvent) => {
    const eventMetric = oldestEvent.metric
    const eventTimestamp = oldestEvent.values[0][0]
    const instanceName = resMgrHosts?.(eventMetric.host_id)?.info?.hostname || eventMetric.instance

    return {
      hostId: eventMetric.host_id,
      phase: eventMetric.phase,
      totalVms: eventMetric.total_vms,
      timestamp: eventMetric.ts,
      vmhaUuid: eventMetric.vmha_uuid,
      cluster: eventMetric.cluster,
      instanceName,
      eventTimestamp,
    }
  })
}

/**
 * Gets the latest event for a host based on timestamp
 */
export const getLatestEventForHost = (
  events: VMHAEventResult[],
  resMgrHosts: (id: string) => any,
): VMHAEvent | null => {
  if (!events || events.length === 0) {
    return null
  }

  // Sort by timestamp (ts field) descending to get the latest
  const sortedEvents = [...events].sort((a, b) => {
    const tsA = parseFloat(a.metric.ts)
    const tsB = parseFloat(b.metric.ts)
    return tsB - tsA
  })

  const latestEvent = sortedEvents[0].metric
  // The event timestamp is in the values array, we take the last value's timestamp for the latest event
  const valuesLength = sortedEvents?.[0]?.values?.length
  const eventTimestamp = sortedEvents?.[0]?.values?.[valuesLength - 1]?.[0]

  const instanceName = resMgrHosts?.(latestEvent.host_id)?.info?.hostname || latestEvent.instance

  return {
    hostId: latestEvent.host_id,
    phase: latestEvent.phase,
    totalVms: latestEvent.total_vms,
    timestamp: latestEvent.ts,
    vmhaUuid: latestEvent.vmha_uuid,
    cluster: latestEvent.cluster,
    instanceName,
    eventTimestamp,
  }
}

/**
 * Gets the latest metrics for each vm_id
 */
function getLatestMetricsByVmId(response: VMHATaskDetailsResponse): VMHATaskResult[] {
  const result: Record<string, VMHATaskResult> = {}

  response.data.result.forEach((item) => {
    const { vm_id, ts } = item.metric
    const timestamp = Number(ts)

    if (!result[vm_id] || timestamp > Number(result[vm_id].metric.ts)) {
      result[vm_id] = item
    }
  })

  return Object.values(result)
}

/**
 * Parses VMHA task details response
 */
export const parseVMHATaskDetailsResponse = (
  response: VMHATaskDetailsResponse,
): VMHATaskDetail[] => {
  if (!response?.data?.result || response.data.result.length === 0) {
    return []
  }

  // Need to sort the metrics by timestamp and get the latest event for each vm_id
  const sortedTasks = getLatestMetricsByVmId(response)

  return sortedTasks.map((task: VMHATaskResult) => ({
    vmId: task.metric.vm_id,
    phase: task.metric.phase,
    timestamp: task.metric.ts,
    reason: task.metric.reason !== 'None' ? task.metric.reason : undefined,
    fault: task.metric.fault !== 'None' ? task.metric.fault : undefined,
    status: task.metric.status,
    task: task.metric.task !== 'None' ? task.metric.task : undefined,
    taskState: task.metric.task_state !== 'None' ? task.metric.task_state : undefined,
    vmState: task.metric.vm_state !== 'None' ? task.metric.vm_state : undefined,
  }))
}

/**
 * Calculates the VMHA banner state based on event phase and task details
 */
export const calculateVMHAState = (event: VMHAEvent, tasks: VMHATaskDetail[]): VMHABannerState => {
  // Check if any tasks have error phase
  const hasErrors = tasks.some((task) => task.phase === VMHATaskPhase.Error)
  if (hasErrors) {
    return VMHABannerState.Error
  }

  // Check if any tasks were skipped
  const hasSkipped = tasks.some((task) => task.phase === VMHATaskPhase.Skipped)
  if (hasSkipped) {
    return VMHABannerState.Warning
  }

  // Or else based on the event phase
  return event.phase === VMHAEventPhase.Completed
    ? VMHABannerState.Completed
    : VMHABannerState.InProgress
}

export interface VMHABannerMessage {
  mainMessage: string
  details: string | JSX.Element
  additionalInfo?: string | JSX.Element
}

/**
 * Extracts the error reason or fault from the first VM task
 */
const getFirstReasonOrFault = (vmTasks: VMHATaskDetail[] = []): string => {
  if (!vmTasks || vmTasks?.length === 0) {
    return ''
  }

  const vmTaskWithReasonOrFault = vmTasks.find((vm) => vm?.reason || vm?.fault)
  return vmTaskWithReasonOrFault?.reason || vmTaskWithReasonOrFault?.fault || ''
}

/**
 * Formats the banner message based on state
 */
export const formatVMHABannerMessage = (
  state: VMHABannerState,
  totalVms: string,
  eventOccurrenceTime: string,
  tasks: VMHATaskDetail[],
): VMHABannerMessage => {
  let failedVMs: VMHATaskDetail[] = []
  let skippedVMs: VMHATaskDetail[] = []

  if ([VMHABannerState.Error, VMHABannerState.Warning].includes(state)) {
    failedVMs = tasks.filter((task) => task.phase === VMHATaskPhase.Error)
    skippedVMs = tasks.filter((task) => task.phase === VMHATaskPhase.Skipped)
  }

  const details = (
    <>
      <Text variant="body2">
        Number of VMs on host:{'  '}
        <b>{totalVms}</b>
      </Text>
      {[VMHABannerState.Error, VMHABannerState.Warning].includes(state) && (
        <>
          <Text variant="body2">
            Number of VMs skipped:{'  '}
            <b>{skippedVMs?.length ?? 0}</b>
          </Text>
          <Text variant="body2">
            Number of VMs unable to migrate:{'  '}
            <b>{failedVMs?.length ?? 0}</b>
          </Text>
        </>
      )}
      <Text variant="body2">
        Event occurrence time:{'  '}
        <b>{eventOccurrenceTime}</b>
      </Text>
    </>
  )

  switch (state) {
    case VMHABannerState.InProgress:
      return {
        mainMessage: 'Host is offline and PCD is attempting VM evacuations.',
        details,
      }

    case VMHABannerState.Completed:
      return {
        mainMessage: 'All VMs evacuated successfully.',
        details,
      }

    case VMHABannerState.Warning: {
      const additionalInfo = (
        <>
          <b>Warning:</b> {getFirstReasonOrFault(skippedVMs) || 'Some VMs were skipped.'}
        </>
      )
      return {
        mainMessage: 'Some VMs were skipped during VM evacuations.',
        details,
        additionalInfo,
      }
    }

    case VMHABannerState.Error: {
      const additionalInfo = (
        <>
          <b>Error:</b> {getFirstReasonOrFault(failedVMs) || 'VM evacuation encountered errors.'}
        </>
      )
      return {
        mainMessage: 'Some VMs encountered errors while attempting to evacuate.',
        details,
        additionalInfo,
      }
    }

    default:
      return {
        mainMessage: '',
        details,
      }
  }
}

// FR-007: Returns Map of hostId → vmhaUuid for the latest event per host
export const getLatestEventByHost = (events: VMHAEvent[]): Map<string, string> => {
  const latest = new Map<string, string>()
  if (!events || events.length === 0) return latest

  events.forEach((event) => {
    const existing = latest.get(event.hostId)
    if (!existing) {
      latest.set(event.hostId, event.vmhaUuid)
    } else {
      const existingEvent = events.find((e) => e.vmhaUuid === existing)
      if (existingEvent && event.eventTimestamp > existingEvent.eventTimestamp) {
        latest.set(event.hostId, event.vmhaUuid)
      }
    }
  })

  return latest
}

/**
 * Formats timestamp to readable date string
 */
export const formatVMHATimestamp = (timestamp: string): string => {
  try {
    // Convert epoch seconds to milliseconds
    const epochSeconds = parseFloat(timestamp)
    const epochMs = Math.floor(epochSeconds * 1000)
    // moment() can handle numeric milliseconds better when passed as a number
    // but formatDate expects a string, so we convert Date object to ISO string
    return formatDate(new Date(epochMs).toISOString())
  } catch (e) {
    return 'Invalid Date'
  }
}

/**
 * Creates a VMHAHostStatus object from event and tasks
 */
export const createVMHAHostStatus = (event: VMHAEvent, tasks: VMHATaskDetail[]): VMHAHostStatus => {
  const state = calculateVMHAState(event, tasks)
  const eventOccurrenceTime = formatVMHATimestamp(event.timestamp)

  return {
    hostId: event.hostId,
    event,
    tasks,
    state,
    eventOccurrenceTime,
  }
}
